/*
 * Api Adapter
 */
import axios from 'axios'

// import ApiHelper from '@/utils/ApiHelper'
import ReposMock from '@/repositories/mock'

// 設定ファイル
/**
 * export const repository = {
 *   baseURL: {string} API接続先URL (required),
 *   isMock: {boolean} モックAPIを使うかどうか (optional, default: false),
 *   timeout: {int} milliseconds (optional, default: 10000),
 *   cacheKey: {string} キャッシュ対策用のリクエストパラメータキー (optional, default: '_c')
 * }
 */
// import {repository as repoEnv} from 'static/config' # e2e:wdioから名前解決ができない
import { repository as repoEnv } from '../../static/config'
import Vue from 'vue'
import {config} from '../repositories/utils/Common'

// IE,Edge対策
// https://developer.mozilla.org/ja/docs/Web/API/FormData#Browser_compatibility
require('formdata-polyfill')

const IS_MOCK = repoEnv.isMock

const _axios = axios
const CancelToken = _axios.CancelToken

/**
 * APIのキャッシュを保存しないようにする
 * https://s8a.jp/no-cache-using-ajax-with-ie11#axios%E3%81%AE%E5%A0%B4%E5%90%88
 */
const setCacheClearToConfig = function (config) {
  const key = repoEnv.cacheKey || '_c'
  config.params = config.params || {}
  if (typeof config.params === 'object') {
    config.params[key] = Date.now()
  }
  return config
}

class ApiAdapter {
  /**
   * コンストラクタ
   */
  constructor () {
    this.http = null

    /**
     * APIキャンセル用のソース
     * @type {null}
     */
    this.source = null

    /**
     * ストレージ（PCでは未使用）
     * @type {null}
     */
    this.store = null

    /**
     * API割り込み（PCでは未使用）
     * @type {null}
     */
    this.ApiInterceptors = null
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param config
   * @returns {*}
   */
  request (config) {
    return this.http.request(config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param config
   * @returns {*}
   */
  get (url, config) {
    // 外部サーバーの画像をmockで返すため自サーバーを装う
    if (IS_MOCK && url.indexOf('//localhost:8765/files/bimage') === 0) {
      url = url.replace('//localhost:8765', '')
    }

    return this.http.get(url, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param config
   * @returns {*}
   */
  delete (url, config) {
    return this.http.delete(url, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param config
   * @returns {*}
   */
  head (url, config) {
    return this.http.head(url, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param config
   * @returns {*}
   */
  options (url, config) {
    return this.http.options(url, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  post (url, data, config) {
    return this.http.post(url, data, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  put (url, data, config) {
    return this.http.put(url, data, config)
  }

  /**
   * HTTPライブラリ（axiosなど）をラップして、ライブラリが変わっても
   * メソッド名が統一されるようにする
   * @see https://github.com/axios/axios#request-method-aliases
   * @param url
   * @param data
   * @param config
   * @returns {*|SemVer.patch|AxiosPromise<any>}
   */
  patch (url, data, config) {
    return this.http.patch(url, data, config)
  }

  /**
   * インスタンス取得
   * @param ApiInterceptors
   * @param store
   * @returns {ApiAdapter}
   */
  instance ({ ApiInterceptors, store }) {
    if (ApiInterceptors) {
      this.ApiInterceptors = ApiInterceptors
    }

    if (store) {
      this.store = store
    }

    this._httpCreate()

    // リクエスト割り込み
    this._setRequestInterceptors()

    // レスポンス割り込み
    this._setRespponseInterceptors()

    // インスタンス後処理
    this._afterInstance()

    return this
  }

  /**
   *
   * @private
   */
  _httpCreate () {
    if (this.http) {
      return
    }

    this.source = CancelToken.source()
    this.http = _axios.create({
      baseURL: this._getBaseUrl(repoEnv.baseURL),
      timeout: repoEnv.timeout || 10000,
      headers: {
        get: {
          'Accept': 'application/json'
        },
        post: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        put: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        delete: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      },
      cancelToken: this.source.token
    })
  }

  /**
   * ベースURL取得
   * @param mixBaseURL
   * @returns {string|*}
   * @private
   */
  _getBaseUrl (mixBaseURL) {
    // 単一のbaseURLが設定されている場合
    if (typeof mixBaseURL === 'string') {
      return mixBaseURL
    }

    // クライアントのURLごとにサーバーのURLが異なる場合
    const baseURL = mixBaseURL[location.hostname]
    if (!baseURL) {
      alert('クライアントのconfig.jsに適切なサーバーが設定されていません。管理者に連絡してください。')
    }

    // 入力されたプロトコルを優先する場合
    if (baseURL.indexOf('//') < 0) {
      return location.protocol + '//' + baseURL
    }

    // プロトコルが固定の場合
    return baseURL
  }

  /**
   * リクエスト前処理をセットする
   * @private
   */
  _setRequestInterceptors () {
    this.http.interceptors.request.use(function (config) {
      // submitボタンをロックする
      Vue.util.lockSubmitButtons(config)
      // APIのキャッシュを保存しないようにする
      config = setCacheClearToConfig(config)
      return config
    },
    function (error) {
      return Promise.reject(error)
    })
  }

  /**
   * レスポンス前処理をセットする
   * @private
   */
  _setRespponseInterceptors () {
    this.http.interceptors.response.use(function (response) {
      // submitボタンのロックを解除する
      Vue.util.unlockSubmitButtons(response)
      return response
    },
    function (error) {
      Vue.util.unlockSubmitButtons(error)
      return Promise.reject(error)
    })
  }

  _afterInstance () {
    if (IS_MOCK) {
      // モック準備
      ReposMock.init(this.http)
    }
  }

  /**
   * トークンセット
   * @param {string} token
   */
  setApiToken (token) {
    this.http.defaults.headers.common['Authorization'] = 'Bearer ' + token
  }

  /**
   * トークン解除
   */
  clearApiToken () {
    delete (this.http.defaults.headers.common['Authorization'])
  }

  /**
   * リクエスト済み＆レスポンス待ちのAPIをキャンセルする
   * （ページ遷移時に利用）
   * @param source
   */
  httpCancel () {
    this.source.cancel(config.lang.apiCanceled)
  }

  /**
   * URLパラメータに管理者フラグをセットする
   * @param isMgmt 管理者フラグ
   * @param params optional 設定済みのパラメーター（マージ用）
   * @returns {*}
   */
  setMgmtParam (isMgmt, params) {
    params = params || {}

    if (isMgmt) {
      params.is_mgmt = '1' // TODO this.app_flag.ON
    }

    return params
  }

  /**
   * フォームデータ送信時のヘッダーを取得する
   */
  getFormHeaders (formData) {
    // ブラウザ以外（wdio）から呼ばれた場合
    if (typeof formData.getHeaders === 'function') {
      //  {'content-type': 'multipart/form-data; boundary=--------------------------494815079172603797323007'}
      return formData.getHeaders()
    }

    return {'Content-Type': 'multipart/form-data'}
  }
}

export default new ApiAdapter()



// WEBPACK FOOTER //
// ./src/plugins/ApiAdapter.js